<template>
  <div class="factory-schema-node" :class="{ 'factory-schema-node--root': depth === 0, 'factory-schema-node--nested': depth > 0 }">
    <v-sheet class="factory-schema-node__row" :class="{ 'factory-schema-node__row--object': isObjectField }" variant="outlined" rounded>
      <v-row align="start" class="ma-0" density="compact">
        <v-col cols="3" class="py-1">
          <div class="factory-schema-node__name-cell">
            <v-btn
              v-if="isObjectField"
              :icon="field.collapsed ? 'mdi-chevron-right' : 'mdi-chevron-down'"
              variant="text"
              density="compact"
              size="small"
              @click="toggleCollapsed"
            />
            <span v-else class="factory-schema-node__name-spacer" />
            <v-text-field
              :model-value="field.name"
              label="Field"
              density="compact"
              variant="outlined"
              hide-details
              @update:model-value="updateName"
            />
          </div>
        </v-col>

        <v-col cols="2" class="py-1">
          <v-select
            :model-value="field.type"
            :items="typeItems"
            item-title="title"
            item-value="value"
            label="Type"
            density="compact"
            variant="outlined"
            hide-details
            @update:model-value="updateType"
          >
            <template #selection="{ item }">
              <div class="factory-schema-node__type-label">
                <v-icon :icon="item.raw.icon" size="small" />
                <span>{{ item.raw.title }}</span>
              </div>
            </template>
            <template #item="{ props, item }">
              <v-list-item v-bind="props" :prepend-icon="item.raw.icon" :title="item.raw.title" />
            </template>
          </v-select>
        </v-col>

        <v-col cols="auto" class="factory-schema-node__required py-1">
          <v-checkbox
            :model-value="field.required"
            label="Required"
            color="primary"
            density="compact"
            hide-details
            @update:model-value="updateRequired"
          />
        </v-col>

        <v-col class="factory-schema-node__description py-1">
          <v-text-field
            :model-value="field.description"
            label="Description"
            density="compact"
            variant="outlined"
            hide-details
            @update:model-value="updateDescription"
          />
        </v-col>

        <v-col cols="auto" class="py-1">
          <div class="factory-schema-node__actions">
            <v-btn v-if="isObjectField" icon="mdi-plus" variant="text" density="compact" size="small" @click="addChild" />
            <v-btn icon="mdi-delete-outline" variant="text" density="compact" size="small" color="error" @click="$emit('delete')" />
          </div>
        </v-col>
      </v-row>
    </v-sheet>

    <div v-if="isObjectField && !field.collapsed" class="factory-schema-node__children">
      <FactorySchemaNode
        v-for="(child, childIndex) in field.children"
        :key="child.id"
        :field="child"
        :depth="depth + 1"
        @update="updateChild(childIndex, $event)"
        @delete="deleteChild(childIndex)"
      />

      <v-btn class="factory-schema-node__add-child" prepend-icon="mdi-plus" variant="outlined" density="compact" @click="addChild">
        Add Nested Field
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import type { PropType } from 'vue'
import {
  createSchemaFieldDraft,
  FACTORY_SCHEMA_TYPE_ITEMS,
  isSchemaFieldType,
  SchemaFieldDraft,
  SchemaFieldType
} from '@/utils/factorySchemaTree'

@Options({
  name: 'FactorySchemaNode',
  props: {
    field: { type: Object as PropType<SchemaFieldDraft>, required: true },
    depth: { type: Number, default: 0 }
  },
  emits: ['update', 'delete']
})
export default class FactorySchemaNode extends Vue {
  field!: SchemaFieldDraft
  depth!: number

  get typeItems() {
    return FACTORY_SCHEMA_TYPE_ITEMS
  }

  get isObjectField(): boolean {
    return this.field.type === 'object'
  }

  updateName(value: string) {
    this.emitUpdatedField({ ...this.field, name: value })
  }

  updateType(value: unknown) {
    if (!isSchemaFieldType(value)) return
    this.emitUpdatedField(this.fieldWithType(value))
  }

  updateRequired(value: unknown) {
    this.emitUpdatedField({ ...this.field, required: value === true })
  }

  updateDescription(value: string) {
    this.emitUpdatedField({ ...this.field, description: value })
  }

  addChild() {
    if (this.field.type !== 'object') return
    this.emitUpdatedField({
      ...this.field,
      children: [...this.field.children, createSchemaFieldDraft('string')],
      collapsed: false
    })
  }

  deleteChild(index: number) {
    this.emitUpdatedField({
      ...this.field,
      children: this.field.children.filter((_, childIndex) => childIndex !== index)
    })
  }

  updateChild(index: number, child: SchemaFieldDraft) {
    this.emitUpdatedField({
      ...this.field,
      children: this.field.children.map((existing, childIndex) => (childIndex === index ? child : existing))
    })
  }

  toggleCollapsed() {
    this.emitUpdatedField({ ...this.field, collapsed: !this.field.collapsed })
  }

  fieldWithType(type: SchemaFieldType): SchemaFieldDraft {
    if (type === 'object') {
      return { ...this.field, type }
    }
    return {
      ...this.field,
      type,
      children: [],
      collapsed: false
    }
  }

  emitUpdatedField(field: SchemaFieldDraft) {
    this.$emit('update', field)
  }
}
</script>

<style lang="scss" scoped>
.factory-schema-node {
  margin-bottom: 10px;
}

.factory-schema-node--nested {
  margin-left: 20px;
}

.factory-schema-node__row {
  border-color: transparent;
  background: transparent;
}

.factory-schema-node__row--object {
  border-color: rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgba(var(--v-theme-surface), 0.18);
}

.factory-schema-node__name-cell,
.factory-schema-node__type-label,
.factory-schema-node__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.factory-schema-node__name-spacer {
  width: 28px;
  flex: 0 0 28px;
}

.factory-schema-node--root .factory-schema-node__name-cell {
  gap: 6px;
}

.factory-schema-node--root .factory-schema-node__name-cell > :deep(.v-btn) {
  width: 24px;
  min-width: 24px;
}

.factory-schema-node--root .factory-schema-node__name-spacer {
  width: 24px;
  flex-basis: 24px;
}

.factory-schema-node__name-cell :deep(.v-input),
.factory-schema-node__description :deep(.v-input) {
  flex: 1 1 auto;
  min-width: 0;
}

.factory-schema-node__required,
.factory-schema-node__actions {
  flex: 0 0 auto;
}

.factory-schema-node__description {
  flex: 1 1 auto;
  min-width: 0;
}

.factory-schema-node__children {
  margin-left: 16px;
  padding-left: 20px;
  border-left: 1px solid rgba(var(--v-border-color), 0.32);
}

.factory-schema-node__add-child {
  width: 100%;
  justify-content: flex-start;
  border-style: dashed;
}
</style>
